import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { quotationAPI, proposalAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { CsfQuickUploadButton } from '../components/CsfQuickUploadButton';
import {
  MagnifyingGlass, Clock, Star, ArrowRight, FileText, Package,
  MapPin, CircleNotch, Trophy, ChatCircle, CheckCircle, PaperPlaneTilt,
  Pencil, HourglassHigh, ShieldWarning,
} from '@phosphor-icons/react';

const statusConfig = {
  draft: { text: 'Borrador', color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300', icon: Pencil },
  sent: { text: 'Enviada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: PaperPlaneTilt },
  in_progress: { text: 'En Proceso', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: HourglassHigh },
  completed: { text: 'Completada', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', icon: CheckCircle },
  cancelled: { text: 'Cancelada', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: FileText },
};

const tabs = [
  { key: 'all', label: 'Todas' },
  { key: 'draft', label: 'Borradores' },
  { key: 'sent', label: 'Enviadas' },
  { key: 'in_progress', label: 'En Proceso' },
  { key: 'completed', label: 'Completadas' },
];

const QuotationsPage = () => {
  const { user, activeRole, isAdmin } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [csfBlock, setCsfBlock] = useState(null);

  const isClientView = (activeRole === 'cliente' || user?.role === 'cliente') && user?.role !== 'admin';
  const isProviderView = activeRole === 'proveedor' || (user?.role === 'proveedor' && activeRole !== 'cliente');

  useEffect(() => {
    fetchQuotations();
  }, [activeRole]);

  const fetchQuotations = async () => {
    setLoading(true);
    setCsfBlock(null);
    try {
      if (isProviderView && !isAdmin) {
        const response = await proposalAPI.getAvailable();
        setQuotations(response.data || []);
      } else {
        const response = await quotationAPI.getAll();
        setQuotations(response.data || []);
      }
    } catch (error) {
      // Intercept CSF gating errors (403 with code=csf_required)
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'csf_required') {
        setCsfBlock(detail);
        setQuotations([]);
      } else {
        console.error('Failed to fetch quotations:', error);
        setQuotations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const tabCounts = useMemo(() => {
    const counts = { all: quotations.length, draft: 0, sent: 0, in_progress: 0, completed: 0 };
    quotations.forEach(q => {
      if (counts[q.status] !== undefined) counts[q.status]++;
    });
    return counts;
  }, [quotations]);

  const filteredQuotations = useMemo(() => {
    let filtered = quotations;
    if (activeTab !== 'all') {
      filtered = filtered.filter(q => q.status === activeTab);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(q => {
        const cats = (q.materials || []).map(m => m.category).filter(Boolean).join(' ');
        return (q.name || '').toLowerCase().includes(query) ||
               (q.category || '').toLowerCase().includes(query) ||
               cats.toLowerCase().includes(query);
      });
    }
    return filtered;
  }, [quotations, activeTab, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quotations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
            {isProviderView ? 'Cotizaciones Disponibles' : 'Mis Cotizaciones'}
          </h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            {isProviderView
              ? 'Encuentra oportunidades que coincidan con tus especialidades'
              : `${quotations.length} cotizaciones en total`}
          </p>
        </div>
        {isClientView && (
          <Link to="/projects/new">
            <Button className="rounded-none bg-orange-500 hover:bg-orange-600" data-testid="new-project-btn">
              <PaperPlaneTilt className="w-4 h-4 mr-2" /> Nuevo Proyecto
            </Button>
          </Link>
        )}
      </div>

      {/* CSF blocking banner for providers without valid Constancia de Situación Fiscal */}
      {csfBlock && (
        <div
          className="border-2 border-orange-500 bg-orange-50 dark:bg-orange-950/30 p-5 sm:p-6 flex flex-col sm:flex-row gap-4"
          data-testid="csf-block-banner"
        >
          <div className="w-12 h-12 bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
            <ShieldWarning className="w-7 h-7" weight="fill" />
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="font-bold text-orange-900 dark:text-orange-200 text-base uppercase tracking-wide">
              {csfBlock.reason === 'csf_expired'
                ? 'Tu Constancia de Situación Fiscal venció'
                : csfBlock.reason === 'csf_invalid'
                  ? 'Tu Constancia de Situación Fiscal no coincide con tus datos'
                  : csfBlock.reason === 'profile_incomplete'
                    ? 'Completa tu perfil de proveedor'
                    : 'Constancia de Situación Fiscal requerida'}
            </h3>
            <p className="text-sm text-orange-800 dark:text-orange-200 leading-relaxed">
              {csfBlock.message}
            </p>
            {Array.isArray(csfBlock.errors) && csfBlock.errors.length > 0 && (
              <ul className="text-xs text-orange-700 dark:text-orange-300 list-disc list-inside space-y-0.5 pt-1">
                {csfBlock.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            <div className="pt-3 flex flex-col sm:flex-row gap-2">
              {csfBlock.reason !== 'profile_incomplete' && (
                <CsfQuickUploadButton testId="csf-quick-upload-btn" onSuccess={fetchQuotations} />
              )}
              <Link to="/profile">
                <Button
                  className="rounded-none bg-orange-600 hover:bg-orange-700 text-white h-10 px-5"
                  data-testid="csf-block-fix-btn"
                >
                  <FileText className="w-4 h-4 mr-2" weight="bold" />
                  Actualizar mi información fiscal
                </Button>
              </Link>
              <a
                href="https://www.sat.gob.mx/aplicacion/login/53027/emite-tu-constancia-de-situacion-fiscal"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center text-sm text-orange-700 dark:text-orange-300 underline h-10 px-2"
              >
                Obtener constancia en el SAT →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {!isProviderView && (
        <div className="flex items-center gap-1 border-b border-zinc-200 dark:border-zinc-700 overflow-x-auto" data-testid="quotation-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
              {tabCounts[tab.key] > 0 && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.key ? 'bg-orange-100 text-orange-700' : 'bg-zinc-100 text-zinc-500'
                }`}>
                  {tabCounts[tab.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
        <Input
          placeholder="Buscar por nombre o categoria..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-none"
          data-testid="search-quotations"
        />
      </div>

      {/* Empty state */}
      {filteredQuotations.length === 0 ? (
        <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
              {activeTab !== 'all'
                ? `No hay cotizaciones con estado "${tabs.find(t => t.key === activeTab)?.label}"`
                : isProviderView ? 'No hay cotizaciones disponibles' : 'No tienes cotizaciones'}
            </h3>
            <p className="text-zinc-500 dark:text-zinc-400">
              {isProviderView
                ? (csfBlock ? 'Actualiza tu información fiscal para ver cotizaciones' : 'Asegúrate de tener tus especialidades configuradas')
                : activeTab === 'draft'
                  ? 'Los borradores aparecen cuando creas un proyecto sin enviarlo'
                  : 'Crea un proyecto para generar cotizaciones'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="quotations-grid">
          {filteredQuotations.map((quotation) => {
            const status = statusConfig[quotation.status] || statusConfig.sent;
            const StatusIcon = status.icon;
            const hasWinner = !!quotation.winner_proposal_id;
            const displayCategory = quotation.category
              || [...new Set((quotation.materials || []).map(m => m.category).filter(Boolean))].join(', ')
              || 'General';
            const isDraft = quotation.status === 'draft';

            return (
              <Link
                key={quotation.quotation_id || quotation.project_id}
                to={isDraft && quotation.project_id
                  ? `/projects/${quotation.project_id}/edit`
                  : `/quotations/${quotation.quotation_id}`}
                data-testid={`quotation-card-${quotation.quotation_id || quotation.project_id}`}
              >
                <Card className={`border-zinc-200 dark:border-zinc-700 rounded-none hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] transition-shadow cursor-pointer h-full ${
                  isDraft && quotation.project_id ? 'border-l-4 border-l-zinc-400' : ''
                }`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant="outline" className="rounded-none bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                            {displayCategory}
                          </Badge>
                          <Badge className={`rounded-none text-xs flex items-center gap-1 ${status.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {status.text}
                          </Badge>
                          {hasWinner && !isProviderView && (
                            <Badge className="bg-green-500 text-white rounded-none text-xs">
                              <Trophy className="w-3 h-3 mr-1" />Ganador
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100 line-clamp-2">
                          {quotation.name}
                        </h3>
                        {quotation.project_name && (
                          <p className="text-sm text-zinc-500 mt-1">Proyecto: {quotation.project_name}</p>
                        )}
                      </div>
                      {isProviderView && (
                        <div className="flex items-center gap-1 text-amber-600 ml-2">
                          <Star className="w-4 h-4" weight="fill" />
                          <span className="text-sm font-medium">{quotation.client_rating?.toFixed(1) || '5.0'}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5 text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-zinc-400" />
                        <span>{quotation.materials?.length || quotation.total_materials || 0} materiales</span>
                      </div>
                      {quotation.deadline && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-zinc-400" />
                          <span>Limite: {new Date(quotation.deadline).toLocaleDateString('es-MX')}</span>
                        </div>
                      )}
                      {quotation.delivery_location?.address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-zinc-400" />
                          <span className="line-clamp-1">{quotation.delivery_location.address}</span>
                        </div>
                      )}
                      {!isProviderView && !isDraft && quotation.proposals_count !== undefined && (
                        <div className="flex items-center gap-2">
                          <ChatCircle className="w-4 h-4 text-zinc-400" />
                          <span>{quotation.proposals_count || 0} propuestas recibidas</span>
                        </div>
                      )}
                    </div>

                    <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
                      <Button className={`w-full rounded-none ${isDraft ? 'bg-zinc-700 hover:bg-zinc-800' : 'btn-primary'}`}>
                        {isDraft && quotation.project_id ? 'Editar Borrador' : isProviderView ? 'Ver Detalles y Cotizar' : 'Ver Detalle'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QuotationsPage;
